import { useState, useEffect } from "react";
import { apiClient } from "@/lib/api-client.js";

// ── Typen ─────────────────────────────────────────────────────────────────────

interface Component {
  id: string;
  tenantId: string | null;
  type: string;
  manufacturer: string;
  modelName: string;
  isActive: boolean;
  specs?: Record<string, unknown>;
}

type ComponentType = "pv_module" | "inverter" | "battery" | "cable" | "other";

interface CreateFormState {
  manufacturer: string;
  modelName: string;
  type: ComponentType;
}

// ── Hilfsfunktionen ───────────────────────────────────────────────────────────

const COMPONENT_TYPE_LABELS: Record<string, string> = {
  pv_module: "PV-Modul",
  inverter: "Wechselrichter",
  battery: "Speicher",
  cable: "Kabel",
  other: "Sonstiges",
};

function typeLabel(type: string): string {
  return COMPONENT_TYPE_LABELS[type] ?? type;
}

// ── Tab-Typen ─────────────────────────────────────────────────────────────────

type Tab = "pv_module" | "inverter" | "all";

const TABS: { id: Tab; label: string }[] = [
  { id: "pv_module", label: "PV-Module" },
  { id: "inverter", label: "Wechselrichter" },
  { id: "all", label: "Alle" },
];

// ── Badge ─────────────────────────────────────────────────────────────────────

function SourceBadge({ isGlobal }: { isGlobal: boolean }) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 600,
        background: isGlobal ? "#dbeafe" : "#dcfce7",
        color: isGlobal ? "#1d4ed8" : "#166534",
      }}
    >
      {isGlobal ? "Global" : "Eigen"}
    </span>
  );
}

// ── Neue Komponente anlegen ───────────────────────────────────────────────────

interface CreateFormProps {
  onCreated: (component: Component) => void;
}

function CreateComponentForm({ onCreated }: CreateFormProps) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<CreateFormState>({
    manufacturer: "",
    modelName: "",
    type: "pv_module",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.manufacturer.trim() || !form.modelName.trim()) {
      setError("Hersteller und Modell sind Pflichtfelder.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiClient.post<Component>("/components", {
        manufacturer: form.manufacturer.trim(),
        modelName: form.modelName.trim(),
        type: form.type,
      });
      if (res.success) {
        onCreated(res.data);
        setForm({ manufacturer: "", modelName: "", type: "pv_module" });
        setOpen(false);
      } else {
        setError("Fehler beim Anlegen der Komponente.");
      }
    } catch {
      setError("Fehler beim Anlegen der Komponente.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        style={{
          padding: "8px 16px",
          borderRadius: 6,
          border: "none",
          background: "var(--color-primary)",
          color: "#fff",
          fontSize: 13,
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        + Neue Komponente
      </button>
    );
  }

  return (
    <div
      style={{
        marginBottom: 24,
        padding: 20,
        borderRadius: 8,
        border: "1px solid var(--color-border)",
        background: "#f8fafc",
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 16, fontSize: 15 }}>Neue Komponente anlegen</div>
      <form onSubmit={(e) => { void handleSubmit(e); }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, display: "block", marginBottom: 4 }}>
              Hersteller *
            </label>
            <input
              type="text"
              value={form.manufacturer}
              onChange={(e) => setForm((f) => ({ ...f, manufacturer: e.target.value }))}
              placeholder="z.B. Suntech"
              style={{ width: "100%", padding: "7px 10px", borderRadius: 6, border: "1px solid var(--color-border)", fontSize: 13 }}
            />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, display: "block", marginBottom: 4 }}>
              Modell *
            </label>
            <input
              type="text"
              value={form.modelName}
              onChange={(e) => setForm((f) => ({ ...f, modelName: e.target.value }))}
              placeholder="z.B. ST-440M"
              style={{ width: "100%", padding: "7px 10px", borderRadius: 6, border: "1px solid var(--color-border)", fontSize: 13 }}
            />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, display: "block", marginBottom: 4 }}>
              Typ *
            </label>
            <select
              value={form.type}
              onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as ComponentType }))}
              style={{ width: "100%", padding: "7px 10px", borderRadius: 6, border: "1px solid var(--color-border)", fontSize: 13 }}
            >
              <option value="pv_module">PV-Modul</option>
              <option value="inverter">Wechselrichter</option>
              <option value="battery">Speicher</option>
              <option value="cable">Kabel</option>
              <option value="other">Sonstiges</option>
            </select>
          </div>
        </div>

        {error && (
          <div style={{ fontSize: 13, color: "#b91c1c", marginBottom: 10 }}>{error}</div>
        )}

        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="submit"
            disabled={submitting}
            style={{
              padding: "8px 16px",
              borderRadius: 6,
              border: "none",
              background: "var(--color-primary)",
              color: "#fff",
              fontSize: 13,
              fontWeight: 600,
              cursor: submitting ? "not-allowed" : "pointer",
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? "Speichere…" : "Speichern"}
          </button>
          <button
            type="button"
            onClick={() => { setOpen(false); setError(null); }}
            style={{
              padding: "8px 16px",
              borderRadius: 6,
              border: "1px solid var(--color-border)",
              background: "#fff",
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Abbrechen
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Komponenten-Tabelle ───────────────────────────────────────────────────────

interface ComponentsTableProps {
  components: Component[];
  onDeactivate: (id: string) => void;
  deactivating: string | null;
}

function ComponentsTable({ components, onDeactivate, deactivating }: ComponentsTableProps) {
  if (components.length === 0) {
    return (
      <div style={{ color: "var(--color-text-muted)", fontSize: 13, textAlign: "center", padding: 40 }}>
        Keine Komponenten vorhanden.
      </div>
    );
  }

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
        <thead>
          <tr style={{ borderBottom: "2px solid var(--color-border)" }}>
            <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: 600 }}>Hersteller</th>
            <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: 600 }}>Modell</th>
            <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: 600 }}>Typ</th>
            <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: 600 }}>Leistung</th>
            <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: 600 }}>Quelle</th>
            <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: 600 }}>Status</th>
            <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: 600 }}></th>
          </tr>
        </thead>
        <tbody>
          {components.map((c) => {
            const isGlobal = c.tenantId === null;
            return (
              <tr key={c.id} style={{ borderBottom: "1px solid var(--color-border)" }}>
                <td style={{ padding: "9px 12px" }}>{c.manufacturer}</td>
                <td style={{ padding: "9px 12px" }}>{c.modelName}</td>
                <td style={{ padding: "9px 12px" }}>{typeLabel(c.type)}</td>
                <td style={{ padding: "9px 12px" }}>
                  {(() => {
                    const s = c.specs;
                    if (!s) return "—";
                    if (c.type === "pv_module" && typeof s.nominalPowerWp === "number") return `${s.nominalPowerWp} Wp`;
                    if (c.type === "inverter" && typeof s.nominalPowerKw === "number") return `${s.nominalPowerKw} kW`;
                    if (c.type === "battery" && typeof s.capacityKwh === "number") return `${s.capacityKwh} kWh`;
                    return "—";
                  })()}
                </td>
                <td style={{ padding: "9px 12px" }}>
                  <SourceBadge isGlobal={isGlobal} />
                </td>
                <td style={{ padding: "9px 12px" }}>
                  <span
                    style={{
                      display: "inline-block",
                      padding: "2px 8px",
                      borderRadius: 4,
                      fontSize: 11,
                      fontWeight: 600,
                      background: c.isActive ? "#dcfce7" : "#fee2e2",
                      color: c.isActive ? "#166534" : "#991b1b",
                    }}
                  >
                    {c.isActive ? "Aktiv" : "Inaktiv"}
                  </span>
                </td>
                <td style={{ padding: "9px 12px" }}>
                  {!isGlobal && (
                    <button
                      onClick={() => onDeactivate(c.id)}
                      disabled={deactivating === c.id}
                      title="Komponente deaktivieren"
                      style={{
                        padding: "4px 10px",
                        borderRadius: 5,
                        border: "1px solid #fca5a5",
                        background: "#fff",
                        color: "#b91c1c",
                        fontSize: 12,
                        cursor: deactivating === c.id ? "not-allowed" : "pointer",
                        opacity: deactivating === c.id ? 0.6 : 1,
                      }}
                    >
                      {deactivating === c.id ? "…" : "Deaktivieren"}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Haupt-Seite ───────────────────────────────────────────────────────────────

export function ComponentsPage() {
  const [activeTab, setActiveTab] = useState<Tab>("pv_module");
  const [components, setComponents] = useState<Component[]>([]);
  const [loading, setLoading] = useState(false);
  const [deactivating, setDeactivating] = useState<string | null>(null);

  const typeFilter = activeTab === "all" ? undefined : activeTab;

  useEffect(() => {
    setLoading(true);
    const path = "/components" + (typeFilter ? `?type=${typeFilter}` : "");
    void apiClient
      .get<Component[]>(path)
      .then((res) => {
        if (res.success) setComponents(Array.isArray(res.data) ? res.data : []);
        else setComponents([]);
      })
      .catch(() => setComponents([]))
      .finally(() => setLoading(false));
  }, [activeTab, typeFilter]);

  const handleCreated = (component: Component) => {
    setComponents((prev) => [component, ...prev]);
  };

  const handleDeactivate = async (id: string) => {
    setDeactivating(id);
    try {
      const res = await apiClient.delete<Component>(`/components/${id}`);
      if (res.success) {
        setComponents((prev) =>
          prev.map((c) => (c.id === id ? { ...c, isActive: false } : c)),
        );
      }
    } catch {
      // silent — component stays in list
    } finally {
      setDeactivating(null);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Komponenten-Katalog</h1>
        <p style={{ color: "var(--color-text-muted)", fontSize: 14, marginTop: 4 }}>
          PV-Module, Wechselrichter und weitere Komponenten verwalten
        </p>
      </div>

      {/* Neue Komponente anlegen */}
      <CreateComponentForm onCreated={handleCreated} />

      {/* Tab-Navigation */}
      <div style={{ display: "flex", gap: 0, marginBottom: 24, borderBottom: "2px solid var(--color-border)" }}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: "10px 20px",
              border: "none",
              borderBottom: activeTab === tab.id ? "2px solid var(--color-primary)" : "2px solid transparent",
              marginBottom: -2,
              background: "transparent",
              color: activeTab === tab.id ? "var(--color-primary)" : "var(--color-text-muted)",
              fontWeight: activeTab === tab.id ? 600 : 400,
              fontSize: 14,
              cursor: "pointer",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab-Inhalt */}
      <div className="card">
        {loading ? (
          <div style={{ color: "var(--color-text-muted)", fontSize: 13, padding: 20 }}>
            Lade Komponenten…
          </div>
        ) : (
          <ComponentsTable
            components={components}
            onDeactivate={(id) => { void handleDeactivate(id); }}
            deactivating={deactivating}
          />
        )}
      </div>
    </div>
  );
}
